import type { UseFormReturn } from 'react-hook-form'
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from '@/components/ui/form'
import { fetchWithAuth } from '@/lib/api'
import { useWorkspace } from '@/providers/workspace-provider'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { CheckCircle2, AlertCircle } from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import { SettingsSection, SettingsSectionRow } from './settings-section'

interface LLMSettingsProps {
  form: UseFormReturn<any>
}

export function LLMSettings({ form }: LLMSettingsProps) {
  const defaultProvider = form.watch('llm.defaultProvider')
  const basecampProvider = form.watch('llm.basecampIngestionProvider')
  const { activeWorkspaceIds } = useWorkspace()
  const discoveryOrgId = activeWorkspaceIds?.[0] || ''

  // Check provider availability
  const [providerStatus, setProviderStatus] = useState({
    anthropic: false,
    openai: false,
    groq: false,
    xai: false,
    localai: false,
    ollama: false,
  })

  const checkProviders = useCallback(async () => {
    try {
      const qs = discoveryOrgId
        ? `?organizationId=${encodeURIComponent(discoveryOrgId)}`
        : ''
      const response = await fetchWithAuth(`/api/llm/providers${qs}`)
      const data = await response.json()
      if (data.success && data.providers) {
        setProviderStatus({
          anthropic: data.providers.find((p: any) => p.id === 'anthropic')?.available || false,
          openai: data.providers.find((p: any) => p.id === 'openai')?.available || false,
          groq: data.providers.find((p: any) => p.id === 'groq')?.available || false,
          xai: data.providers.find((p: any) => p.id === 'xai')?.available || false,
          localai: data.providers.find((p: any) => p.id === 'localai')?.available || false,
          ollama: data.providers.find((p: any) => p.id === 'ollama')?.available || false,
        })
      }
    } catch (error) {
      console.warn('Could not check provider status:', error)
    }
  }, [discoveryOrgId])

  useEffect(() => {
    checkProviders()
  }, [checkProviders])

  const enabledProviders = form.watch('llm.enabledProviders') || ['anthropic']

  const toggleProvider = (providerId: string, enabled: boolean) => {
    const current = enabledProviders || []
    const next = enabled
      ? current.includes(providerId)
        ? current
        : [...current, providerId]
      : current.filter((p: string) => p !== providerId)
    form.setValue('llm.enabledProviders', next, { shouldDirty: true, shouldTouch: true })
  }

  const providerLabels: Record<string, string> = {
    anthropic: 'Claude (Anthropic)',
    openai: 'OpenAI',
    groq: 'Groq (Fast & Cheap)',
    xai: 'xAI (Grok)',
    localai: 'Custom endpoint',
    ollama: 'Ollama (Local)'
  }

  return (
    <div className="space-y-6">
      {/* Provider Enable/Disable Section */}
      <SettingsSection
        title="Chat Providers"
        description={
          <>
            Enable providers in the chat model picker. Availability reflects the{' '}
            <strong>API server</strong> environment (Railway env on flownotes.space; local{' '}
            <code className="text-xs bg-muted px-1 rounded">.env</code> when developing). Keys in
            your laptop <code className="text-xs bg-muted px-1 rounded">.env</code> do not apply when
            the webapp talks to production.
          </>
        }
      >
        <div className="space-y-2">
          {(['anthropic', 'openai', 'groq', 'xai', 'localai', 'ollama'] as const).map((providerId) => {
            const isEnabled = enabledProviders.includes(providerId)
            const isAvailable = providerStatus[providerId as keyof typeof providerStatus] || false

            return (
              <SettingsSectionRow
                key={providerId}
                selected={isEnabled}
                className="flex items-center justify-between"
              >
                  <div className="flex items-center gap-3">
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={(checked) => toggleProvider(providerId, checked)}
                    disabled={!isAvailable}
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{providerLabels[providerId]}</span>
                      {isAvailable ? (
                        <Badge variant="outline" className="text-emerald-600 border-emerald-600/50">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Available
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground border-border">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Not Configured
                        </Badge>
                      )}
                    </div>
                    {!isAvailable && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {providerId === 'ollama'
                          ? 'Start Ollama (ollama serve) and ensure OLLAMA_URL is set in .env if needed.'
                          : providerId === 'localai'
                            ? 'Set LLM_BASE_URL on the active workspace Secrets (Custom endpoint). Availability follows that workspace, not Railway env.'
                          : providerId === 'xai' || providerId === 'groq' || providerId === 'openai' || providerId === 'anthropic'
                            ? `Set ${
                                (
                                  {
                                    anthropic: 'ANTHROPIC_API_KEY',
                                    openai: 'OPENAI_API_KEY',
                                    groq: 'GROQ_API_KEY',
                                    xai: 'XAI_API_KEY',
                                  } as const
                                )[providerId]
                              } in API .env or Settings > Secrets (BYOK).`
                          : 'Configure API key in environment variables to enable'}
                      </p>
                    )}
                  </div>
                </div>
              </SettingsSectionRow>
            )
          })}
        </div>

        <FormDescription>
          A platform API key puts that provider in the chat picker. OpenAI uses the same{' '}
          <code className="text-xs bg-muted px-1 rounded">OPENAI_API_KEY</code> as Whisper. Toggles are
          saved to your account. Availability still requires a key on the API (Railway env or Secrets).
        </FormDescription>
      </SettingsSection>

      <div>
        <h3 className="text-lg font-semibold mb-2">Default chat provider</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Default provider and model for new chat sessions. Must be enabled above.
        </p>

        <FormField
          control={form.control}
          name="llm.defaultProvider"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Provider</FormLabel>
              <FormControl>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value)
                    form.setValue('llm.chatProvider', value as 'anthropic' | 'openai' | 'groq' | 'xai' | 'ollama')
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="anthropic">
                      <div className="flex items-center gap-2">
                        <span>Claude (Anthropic)</span>
                        {providerStatus.anthropic ? (
                          <Badge variant="outline" className="ml-auto text-green-600 border-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Available
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="ml-auto text-red-600 border-red-600">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Not Configured
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                    <SelectItem value="openai">
                      <div className="flex items-center gap-2">
                        <span>OpenAI (GPT-4o)</span>
                        {providerStatus.openai ? (
                          <Badge variant="outline" className="ml-auto text-green-600 border-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Available
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="ml-auto text-red-600 border-red-600">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Not Configured
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                    <SelectItem value="groq">
                      <div className="flex items-center gap-2">
                        <span>Groq (Fast & Cheap)</span>
                        {providerStatus.groq ? (
                          <Badge variant="outline" className="ml-auto text-green-600 border-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Available
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="ml-auto text-red-600 border-red-600">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Not Configured
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                    <SelectItem value="xai">
                      <div className="flex items-center gap-2">
                        <span>xAI (Grok)</span>
                        {providerStatus.xai ? (
                          <Badge variant="outline" className="ml-auto text-green-600 border-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Available
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="ml-auto text-red-600 border-red-600">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Not Configured
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                    <SelectItem value="ollama">
                      <div className="flex items-center gap-2">
                        <span>Ollama (Local)</span>
                        {providerStatus.ollama ? (
                          <Badge variant="outline" className="ml-auto text-green-600 border-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Available
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="ml-auto text-red-600 border-red-600">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Not Configured
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormDescription>
                Claude is recommended for better instruction following and tool calling.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Model selection based on provider */}
        {defaultProvider === 'anthropic' && (
          <FormField
            control={form.control}
            name="llm.defaultModel"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Claude Model</FormLabel>
                <FormControl>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="claude-sonnet-5-5">Claude Sonnet 5.5 (Recommended)</SelectItem>
                      <SelectItem value="claude-opus-5-5">Claude Opus 5.5</SelectItem>
                      <SelectItem value="claude-fable-5-1">Claude Fable 5.1</SelectItem>
                      <SelectItem value="claude-haiku-5-5">Claude Haiku 5.5 (Fast)</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormDescription>
                  Latest model recommended for best quality.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {defaultProvider === 'xai' && (
          <FormField
            control={form.control}
            name="llm.defaultModel"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Grok Model</FormLabel>
                <FormControl>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grok-4.6">Grok 4.6 (Recommended)</SelectItem>
                      <SelectItem value="grok-4.5">Grok 4.5</SelectItem>
                      <SelectItem value="grok-4.3">Grok 4.3</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormDescription>
                  Opt-in experiment. Requires XAI_API_KEY (env or workspace secret).
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      {/* Basecamp Ingestion Provider */}
      <div>
        <h3 className="text-lg font-semibold mb-2">Basecamp Ingestion</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Choose AI provider for converting Basecamp todos to work specs.
        </p>

        <FormField
          control={form.control}
          name="llm.basecampIngestionProvider"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Provider</FormLabel>
              <FormControl>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="anthropic">Claude (Recommended)</SelectItem>
                    <SelectItem value="openai">OpenAI GPT-4o</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormDescription>
                Claude produces higher quality work specs with better instruction following.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Ollama local testing */}
      <div className="rounded-lg p-4 bg-muted/30">
        <h3 className="text-lg font-semibold mb-2">Ollama (local testing)</h3>
        <p className="text-sm text-muted-foreground mb-3">
          Use a local model (e.g. Qwen, Llama) with no API key. Good for privacy and testing.
        </p>
        <ul className="text-sm list-disc list-inside space-y-1 text-muted-foreground">
          <li>Enable &quot;Ollama (Local)&quot; above, then choose it as Chat provider or in the chat pane.</li>
          <li>In chat, select provider &quot;Ollama&quot; and pick a model from the dropdown (models come from your local Ollama).</li>
          <li>Optional: in <code className="text-xs bg-muted px-1 rounded">.env</code> set <code className="text-xs bg-muted px-1 rounded">OLLAMA_URL</code> (default <code className="text-xs bg-muted px-1 rounded">http://127.0.0.1:11434</code>) and <code className="text-xs bg-muted px-1 rounded">OLLAMA_MODEL</code> (e.g. <code className="text-xs bg-muted px-1 rounded">qwen35-a3b</code>). Restart the API server after changes.</li>
        </ul>
      </div>
    </div>
  )
}

